/**
 * Make a setter for nested property
 *
 * Mutates obj.
 * Marked as unsafe because partially optional objects are not properly sanitized, see example.
 *
 * @example
 * interface Foo {
 *   a?: {
 *     b?: string;
 *     c: string;
 *   }
 * }
 *
 * const x: Foo = {};
 * unsafeSetDeepProp(x, 'a', 'b', 'hello world');
 * // x is now {a: {b: 'hello world'}}
 * // which is not compliant with Foo interface, since a.c is mandatory if a is present.
 */
export const unsafeSetDeepProp: {
  <T, K1 extends keyof T, K2 extends keyof Required<T>[K1]>(
    obj: T,
    k1: K1,
    k2: K2,
  ): (value: Required<Required<T>[K1]>[K2]) => void;

  <
    T,
    K1 extends keyof T,
    K2 extends keyof Required<T>[K1],
    K3 extends keyof Required<Required<T>[K1]>[K2],
  >(
    obj: T,
    k1: K1,
    k2: K2,
    k3: K3,
  ): (value: Required<Required<Required<T>[K1]>[K2]>[K3]) => void;

  <
    T,
    K1 extends keyof T,
    K2 extends keyof Required<T>[K1],
    K3 extends keyof Required<Required<T>[K1]>[K2],
    K4 extends keyof Required<Required<Required<T>[K1]>[K2]>[K3],
  >(
    obj: T,
    k1: K1,
    k2: K2,
    k3: K3,
    k4: K4,
  ): (value: Required<Required<Required<Required<T>[K1]>[K2]>[K3]>[K4]) => void;
} = (obj: any, ...keys: (keyof any)[]) => {
  const k = [...keys];
  const lastKey = k.pop()!;
  const lastLevel = k.reduce((acc: any, key) => {
    if (!(key in acc)) {
      acc[key] = {};
    }
    return acc[key];
  }, obj);
  return (value: any) => {
    lastLevel[lastKey] = value;
  };
};
